import React, { FC, useEffect } from "react";
import Tealium from "@4tn/webx-analytics";
import { Theme, styled, useMediaQuery } from "@mui/material";
import useSearchValues from "@navigation/SearchModal/useSearchValues";
import { useInfiniteQuery } from "@tanstack/react-query";
import AspectRatioContainer from "@common/AspectRatioContainer";
import { PrimaryButton } from "@common/Buttons";
import OopsPage from "@pageContent/OopsPage";
import { InfiniteCollectionContainer, ItemsContainer } from "@pageContent/common/InfiniteCollection/InfiniteCollection";
import {
  ArticleImage,
  ImageContainer,
  ItemCategory,
  ItemContainer,
  ItemTitle,
  PlayIcon,
  TextContainer,
} from "@pageContent/common/InfiniteCollection/InfiniteCollectionItem/InfiniteCollectionItem";
import { CollectionSkeleton } from "@pageContent/common/Skeletons/InfiniteCollectionSkeleton";
import TextSkeleton from "@pageContent/common/Skeletons/TextSkeleton";
import { BUTTON_SIZE, VARIANT } from "@constants/button";
import { IconEnum, RESULTS_FOR, SHOW_MORE_SEARCH_RESULTS } from "@constants/consts";
import { colors, fonts } from "@constants/cssVariables";
import { TEALIUM_EVENT_CATEGORY, TEALIUM_EVENT_LABEL, TEALIUM_EVENT_NAME } from "@constants/tealium.constants";
import { useHostConfig } from "@utils/common/getHostConfig";
import { useNoticeError } from "@utils/common/newRelic";
import { getDateLabel } from "@utils/pageContent/dateLabel";
import { paginatedFetch } from "@utils/pageContent/fetchData";
import NoSearchResult from "../NoSearchResult";
import SearchModalPlaceholder from "../SearchModalPlaceholder";
import { searchTabValues } from "../SearchModalTabBar/SearchModalTabBar";

const Title = styled("h3")(({ theme }) => ({
  font: fonts.bodyMedium,
  color: colors.neutral90,
  alignSelf: "flex-start",

  [theme.breakpoints.up("md")]: {
    marginBottom: theme.spacing(2),
  },
}));

const DetailsContainer = styled(TextContainer)(({ theme }) => ({
  flexDirection: "column",
  gap: 0,
  [theme.breakpoints.up("md")]: {
    gap: theme.spacing(1),
  },
}));

const SearchContainer = styled(InfiniteCollectionContainer, {
  shouldForwardProp: (prop) => prop !== "isLoading" && prop !== "isInitialScreen",
})<{ isLoading?: boolean; isInitialScreen?: boolean }>(({ theme, isLoading, isInitialScreen }) => ({
  margin: theme.spacing(isLoading && isInitialScreen ? 2 : 16, 0),

  [theme.breakpoints.down("sm")]: {
    [`${ItemContainer}`]: {
      maxHeight: 76,
    },

    [`${DetailsContainer}`]: {
      display: "block",
    },
  },

  [theme.breakpoints.up("sm")]: {
    margin: theme.spacing(isLoading && isInitialScreen ? 4 : 16, 0),
  },
}));

const ItemDate = styled("div")(({ theme }) => ({
  font: fonts.bodySmall,
  color: colors.neutral40,
  alignSelf: "flex-start",
  marginBottom: theme.spacing(4),
}));

const trackClick = (position: number) => {
  Tealium.link({
    event_name: TEALIUM_EVENT_NAME.SEARCH_QUERY,
    event_category: TEALIUM_EVENT_CATEGORY.SEARCH,
    event_label: TEALIUM_EVENT_LABEL.SEARCH_RESULTS,
    event_value: position,
  });
};

const trackSearchValue = (searchValue: string) => {
  Tealium.link({
    event_name: TEALIUM_EVENT_NAME.SEARCH_QUERY,
    event_category: TEALIUM_EVENT_CATEGORY.SEARCH,
    event_label: searchValue,
  });
};

const fetchLinks = async (
  searchValue: string,
  contentApiEndpoint: string,
  pageParam: number,
  tabContentType: string
) => {
  trackSearchValue(searchValue);
  const endpoint = `${contentApiEndpoint}/search/` + searchValue;

  if (searchValue) {
    const data = await paginatedFetch<PageLink>(endpoint, limit, pageParam, tabContentType);
    data.items = data.items.map((item) => ({ ...item, createdAt: getDateLabel(item.createdAt || "") }));
    return data;
  }

  return {
    items: [],
    nextPage: limit,
    totalResults: 0,
  };
};

const limit = 24;

const SearchModalResult: FC = () => {
  const { searchParam, tabParam } = useSearchValues();
  const tabContentType = searchTabValues[tabParam].contentType;
  const { contentApiEndpoint } = useHostConfig();
  const isMediumAndUp = useMediaQuery<Theme>((theme) => theme.breakpoints.up("md"));
  const searchModalResultQueryKey = ["search", tabContentType, searchParam];

  const { data, isLoading, isError, fetchNextPage, hasNextPage, isFetchingNextPage, error } = useInfiniteQuery({
    queryKey: searchModalResultQueryKey,
    queryFn: ({ pageParam }: { pageParam: number }) =>
      fetchLinks(searchParam, contentApiEndpoint, pageParam, tabContentType),
    initialPageParam: 0,
    getNextPageParam: (lastPage) => lastPage.nextPage,
  });

  useNoticeError(error, { queryKey: searchModalResultQueryKey.join(","), searchParam, tabParam });

  const [firstPage] = data?.pages || [];

  useEffect(() => {
    window.eventBus.dispatch("searchResults", { hasResults: !!firstPage?.items.length });
  }, [firstPage]);

  const title = `${Number(firstPage?.totalResults) > 100 ? "100+" : firstPage?.totalResults} ${RESULTS_FOR} `;

  const loadMore = () => {
    Tealium.link({
      event_name: TEALIUM_EVENT_NAME.CONTENT_LISTING_MORE,
      event_category: TEALIUM_EVENT_CATEGORY.CONTENT_LISTING,
      event_label: TEALIUM_EVENT_LABEL.MORE_RESULTS,
    });
    fetchNextPage();
  };

  if (isLoading && !firstPage?.items.length)
    return (
      <SearchContainer isLoading isInitialScreen={!searchParam}>
        <TextSkeleton height={24} width={177} sx={{ alignSelf: "flex-start" }} />
        <ItemsContainer>
          <CollectionSkeleton title={title} limit={limit} showCategory={isMediumAndUp} />
        </ItemsContainer>
      </SearchContainer>
    );

  if (isError) return <OopsPage />;

  if (!searchParam) return <SearchModalPlaceholder />;

  if (searchParam && firstPage?.items.length === 0 && !isLoading && !isFetchingNextPage)
    return <NoSearchResult currentTab={tabParam} />;

  return (
    <SearchContainer>
      <Title>
        {title} <b>&lsquo;{searchParam}&rsquo;</b>
      </Title>
      <ItemsContainer>
        {data?.pages.map((page, index) => (
          <React.Fragment key={index}>
            {page.items?.map(({ id, url, hasVideo, imageUrl, category, title: linkTitle, createdAt }, index) => (
              <ItemContainer key={id} href={url} onClick={() => trackClick(index + 1)}>
                <ImageContainer>
                  {hasVideo && (
                    <PlayIcon
                      icon={IconEnum.PLAY_ICON_FILLED}
                      size={BUTTON_SIZE.EXTRA_SMALL}
                      variant={VARIANT.PRIMARY}
                      color={colors.neutral0}
                      isNonInteractive
                    />
                  )}
                  <AspectRatioContainer aspectRatio="16:9">
                    <ArticleImage
                      src={imageUrl}
                      alt={title}
                      fill
                      sizes="(max-width: 900px) 33vw, (max-width: 1200px) 25vw, 300px"
                    />
                  </AspectRatioContainer>
                </ImageContainer>
                <DetailsContainer>
                  <ItemTitle>{linkTitle}</ItemTitle>
                  {createdAt && <ItemDate>{createdAt}</ItemDate>}
                  <ItemCategory>{category?.title}</ItemCategory>
                </DetailsContainer>
              </ItemContainer>
            ))}
          </React.Fragment>
        ))}
        {isFetchingNextPage && <CollectionSkeleton title={title} limit={limit} showCategory />}
      </ItemsContainer>
      {hasNextPage && (
        <PrimaryButton fullWidth disabled={isLoading || isFetchingNextPage} onClick={loadMore}>
          {SHOW_MORE_SEARCH_RESULTS}
        </PrimaryButton>
      )}
    </SearchContainer>
  );
};

export default SearchModalResult;
